<template>
  <div ref="containerElement" class="w-full" style="text-align: left" />
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import * as monaco from 'monaco-editor'

import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useEditorPageStatePresenter } from '@/presentation/composables/editor/useEditorPageStatePresenter'

monaco.languages.register({
  id: 'haskell'
})
monaco.languages.register({
  id: 'd'
})
monaco.languages.register({
  id: 'scala'
})
monaco.languages.register({
  id: 'ocaml'
})

export default defineComponent({
  props: {
    modelSourceCode: {
      type: String,
      required: true
    }
  },
  setup(props, ctx) {
    const containerElement = ref<HTMLElement | null>(null)
    let diffEditor: monaco.editor.IStandaloneDiffEditor
    const { viewMode, editorFontSize } = useConfigStatePresenter()
    const { sourceCode } = useEditorPageStatePresenter()

    const originalModel = monaco.editor.createModel(props.modelSourceCode)
    const modifiedModel = monaco.editor.createModel(sourceCode.value)

    const initMonaco = () => {
      if (containerElement.value != null) {
        diffEditor = monaco.editor.createDiffEditor(containerElement.value, {
          theme: viewMode.value == 'LIGHT' ? 'vs' : 'vs-dark',
          automaticLayout: true,
          scrollBeyondLastLine: false,
          originalEditable: false,
          readOnly: true,
          lineNumbers: 'on',
          lineNumbersMinChars: 3,
          lineDecorationsWidth: 10,
          minimap: { enabled: false }
        })
        diffEditor.setModel({
          original: originalModel,
          modified: modifiedModel
        })

        setFontSize(editorFontSize.value)
      }
    }

    const destroyMonaco = () => {
      if (typeof diffEditor !== 'undefined') {
        diffEditor.dispose()
      }
    }

    const setFontSize = (size: number) => {
      let options = { fontSize: size }
      diffEditor.updateOptions(options)
    }

    watch(
      () => viewMode.value,
      () => {
        destroyMonaco()
        initMonaco()
      }
    )

    watch(
      () => editorFontSize.value,
      () => {
        setFontSize(editorFontSize.value)
      }
    )

    watch(
      () => sourceCode.value,
      newValue => {
        if (diffEditor) {
          if (newValue !== diffEditor.getModifiedEditor().getValue()) {
            diffEditor.getModifiedEditor().setValue(newValue)
          }
        }
      }
    )

    onMounted(() => {
      initMonaco()
    })

    onBeforeUnmount(() => {
      destroyMonaco()
    })

    return {
      containerElement
    }
  }
})
</script>
